import React, { useState, useEffect, useRef, useMemo } from 'react';
import Tippy from '@tippyjs/react/headless';
import { useGeoStore } from '@/stores/geoStore';

export interface CityCascaderProps {
  /** 绑定的城市 ID (geo_city_id) */
  value?: number | string;
  /** 选中值变化回调 */
  onChange?: (cityId: number | undefined, city?: Api.Geo.GeoCityResponse, province?: Api.Geo.GeoProvinceResponse) => void;
  /** 占位提示文案 */
  placeholder?: string;
  /** 是否可清空 */
  clearable?: boolean;
  /** 是否禁用 */
  disabled?: boolean;
  /** 左侧前置图标 */
  icon?: React.ReactNode;
  /** 最外层容器类名 */
  className?: string;
  /** 触发器按钮类名 */
  selectClassName?: string;
  /** 弹出位置方向，不传则由 Tippy 自动控制 */
  placement?: 'auto' | 'auto-start' | 'auto-end' | 'top' | 'top-start' | 'top-end' | 'bottom' | 'bottom-start' | 'bottom-end' | 'right' | 'right-start' | 'right-end' | 'left' | 'left-start' | 'left-end';
}

export const CityCascader: React.FC<CityCascaderProps> = ({
  value,
  onChange,
  placeholder = '请选择所在省份/城市',
  clearable = true,
  disabled = false,
  icon,
  className = '',
  selectClassName = '',
  placement,
}) => {
  const { provinces, fetchProvinces, loading } = useGeoStore();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedProvinceId, setSelectedProvinceId] = useState<number | string | null>(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  // 初始化加载省市数据
  useEffect(() => {
    if (provinces.length === 0) {
      fetchProvinces().catch(err => console.error('获取省市列表失败:', err));
    }
  }, [provinces.length, fetchProvinces]);

  // 根据当前 value 寻找省份和城市
  let currentProvince: Api.Geo.GeoProvinceResponse | undefined;
  let currentCity: Api.Geo.GeoCityResponse | undefined;

  if (value !== undefined && value !== null && value !== '') {
    const numId = Number(value);
    for (const prov of provinces) {
      if (prov.children) {
        const foundCity = prov.children.find(c => c.id === numId || String(c.id) === String(value));
        if (foundCity) {
          currentCity = foundCity;
          currentProvince = prov;
          break;
        }
      }
    }
  }

  // 面板打开时自动定位省份并聚焦搜索框
  useEffect(() => {
    if (isOpen) {
      if (currentProvince) {
        setSelectedProvinceId(currentProvince.id);
      } else if (provinces.length > 0 && !selectedProvinceId) {
        setSelectedProvinceId(provinces[0].id);
      }
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    } else {
      setSearchKeyword('');
    }
  }, [isOpen, currentProvince, provinces, selectedProvinceId]);

  const activeProvince = provinces.find(p => p.id === selectedProvinceId || String(p.id) === String(selectedProvinceId)) || provinces[0];

  const handleSelectCity = (city: Api.Geo.GeoCityResponse, prov: Api.Geo.GeoProvinceResponse) => {
    if (disabled) return;
    onChange?.(city.id, city, prov);
    setIsOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    onChange?.(undefined);
  };

  // 搜索关键字匹配过滤
  const searchKeywordTrimmed = searchKeyword.trim().toLowerCase();
  const searchResults = useMemo(() => {
    if (!searchKeywordTrimmed) return [];
    const results: { city: Api.Geo.GeoCityResponse; province: Api.Geo.GeoProvinceResponse }[] = [];
    for (const prov of provinces) {
      if (!prov.children) continue;
      const matchProv = prov.province_name.toLowerCase().includes(searchKeywordTrimmed);
      for (const city of prov.children) {
        const matchCity = city.city_name.toLowerCase().includes(searchKeywordTrimmed);
        if (matchProv || matchCity) {
          results.push({ city, province: prov });
        }
      }
    }
    return results;
  }, [provinces, searchKeywordTrimmed]);

  // 显示选中的文本
  const displayText = currentCity && currentProvince
    ? currentProvince.province_name === currentCity.city_name
      ? currentCity.city_name
      : `${currentProvince.province_name} / ${currentCity.city_name}`
    : '';

  return (
    <div className={`relative w-full ${className}`}>
      <Tippy
        interactive={true}
        visible={isOpen}
        onClickOutside={() => setIsOpen(false)}
        placement={placement}
        offset={[0, 6]}
        appendTo={() => document.body}
        render={(attrs) => (
          <div
            {...attrs}
            className="z-[100] w-[380px] max-w-[calc(100vw-32px)] bg-white rounded-xl border border-background-200 shadow-2xl overflow-hidden flex flex-col animate-in fade-in duration-150 outline-none"
          >
            {/* 搜索框 */}
            <div className="p-2.5 bg-gray-50 border-b border-background-200">
              <div className="relative flex items-center">
                <i className="ri-search-line absolute left-3 text-foreground-400 text-xs pointer-events-none"></i>
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  placeholder="搜索省份或城市名称（如：北京、深圳、杭州）..."
                  className="w-full pl-8 pr-7 py-1.5 rounded-lg border border-gray-200 bg-white text-xs text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                />
                {searchKeyword && (
                  <button
                    type="button"
                    onClick={() => setSearchKeyword('')}
                    className="absolute right-2 text-foreground-300 hover:text-foreground-500 text-xs transition-colors cursor-pointer"
                  >
                    <i className="ri-close-circle-fill"></i>
                  </button>
                )}
              </div>
            </div>

            {/* 搜索模式与级联选择模式切换 */}
            {searchKeywordTrimmed ? (
              /* 搜索结果列表 */
              <div className="h-64 overflow-y-auto py-1">
                <div className="px-3 py-1 text-[11px] font-semibold text-foreground-400 bg-gray-50/50">
                  搜索结果 ({searchResults.length})
                </div>
                {searchResults.length > 0 ? (
                  searchResults.map(({ city, province }) => {
                    const isCitySelected = currentCity?.id === city.id || String(currentCity?.id) === String(city.id);
                    const nameText = province.province_name === city.city_name
                      ? city.city_name
                      : `${province.province_name} / ${city.city_name}`;
                    return (
                      <button
                        key={`${province.id}-${city.id}`}
                        type="button"
                        onClick={() => handleSelectCity(city, province)}
                        className={`w-full text-left px-4 py-2 text-xs flex items-center justify-between transition-colors cursor-pointer ${
                          isCitySelected
                            ? 'bg-primary-50 text-primary-700 font-semibold'
                            : 'text-foreground-700 hover:bg-primary-50/40'
                        }`}
                      >
                        <span className="truncate">{nameText}</span>
                        {isCitySelected && <i className="ri-check-line text-primary-600 text-sm"></i>}
                      </button>
                    );
                  })
                ) : (
                  <div className="py-12 text-center text-xs text-foreground-400 flex flex-col items-center gap-1.5">
                    <i className="ri-search-2-line text-2xl text-gray-300"></i>
                    <span>未找到与“{searchKeyword}”匹配的省市</span>
                  </div>
                )}
              </div>
            ) : (
              /* 标准双列联级选择区域 */
              <>
                {/* 面板列标题栏 */}
                <div className="px-4 py-2 bg-gray-50/80 border-b border-background-200 flex items-center justify-between text-[11px] font-medium text-foreground-400">
                  <span className="w-1/2">选择省份 / 直辖市</span>
                  <span className="w-1/2 pl-3">选择城市</span>
                </div>

                <div className="flex h-64 overflow-hidden">
                  {/* 左侧：省份列表 */}
                  <div className="w-1/2 border-r border-background-200 overflow-y-auto py-1">
                    {provinces.map(prov => {
                      const isSelected = selectedProvinceId === prov.id || String(selectedProvinceId) === String(prov.id);
                      return (
                        <button
                          key={prov.id}
                          type="button"
                          onMouseEnter={() => setSelectedProvinceId(prov.id)}
                          onClick={() => setSelectedProvinceId(prov.id)}
                          className={`w-full text-left px-3.5 py-2 text-xs flex items-center justify-between transition-colors cursor-pointer ${
                            isSelected
                              ? 'bg-primary-50 text-primary-700 font-semibold'
                              : 'text-foreground-700 hover:bg-gray-50'
                          }`}
                        >
                          <span className="truncate">{prov.province_name}</span>
                          {isSelected && <i className="ri-arrow-right-s-line text-primary-500 text-sm"></i>}
                        </button>
                      );
                    })}
                  </div>

                  {/* 右侧：对应城市列表 */}
                  <div className="w-1/2 overflow-y-auto py-1 bg-white">
                    {activeProvince && activeProvince.children && activeProvince.children.length > 0 ? (
                      activeProvince.children.map(city => {
                        const isCitySelected = currentCity?.id === city.id || String(currentCity?.id) === String(city.id);
                        return (
                          <button
                            key={city.id}
                            type="button"
                            onClick={() => handleSelectCity(city, activeProvince)}
                            className={`w-full text-left px-4 py-2 text-xs flex items-center justify-between transition-colors cursor-pointer ${
                              isCitySelected
                                ? 'bg-primary-50 text-primary-700 font-semibold'
                                : 'text-foreground-700 hover:bg-primary-50/40'
                            }`}
                          >
                            <span className="truncate">{city.city_name}</span>
                            {isCitySelected && <i className="ri-check-line text-primary-600 text-sm"></i>}
                          </button>
                        );
                      })
                    ) : (
                      <div className="p-4 text-center text-xs text-foreground-400">暂无城市下辖数据</div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      >
        {/* 触发器按钮 */}
        <button
          type="button"
          disabled={disabled || loading}
          onClick={() => !disabled && !loading && setIsOpen(prev => !prev)}
          className={`w-full flex items-center justify-between bg-white border border-background-300 text-left outline-none transition-all duration-200 cursor-pointer ${
            selectClassName || 'h-[42px] px-4 rounded-lg text-sm'
          } ${
            isOpen
              ? 'border-primary-400 ring-2 ring-primary-100 shadow-sm'
              : 'hover:border-background-400'
          } ${
            disabled || loading ? 'bg-gray-50 text-foreground-400 cursor-not-allowed border-gray-200' : 'text-foreground-950'
          }`}
        >
          <span className={`truncate flex-1 pr-2 flex items-center gap-1.5 ${!displayText ? 'text-foreground-400' : ''}`}>
            {icon}
            <span className="truncate">{loading ? '正在加载城市数据...' : displayText || placeholder}</span>
          </span>
          <div className="flex items-center gap-1.5 shrink-0 text-foreground-400">
            {clearable && displayText && !disabled && (
              <i
                onClick={handleClear}
                className="ri-close-circle-fill text-foreground-300 hover:text-foreground-500 text-base cursor-pointer transition-colors"
                title="清空"
              ></i>
            )}
            <i className={`ri-arrow-down-s-line text-lg transition-transform duration-200 ${isOpen ? 'rotate-180 text-primary-500' : ''}`}></i>
          </div>
        </button>
      </Tippy>
    </div>
  );
};

export default CityCascader;
